"use client";

import type { ReactNode } from "react";
import { cn } from "@/lib/utils";

export interface FieldShellProps {
  label: string;
  hint?: string;
  htmlFor?: string;
  errors?: string[];
  counter?: { length: number; max: number };
  aside?: ReactNode;
  inline?: boolean;
  children: ReactNode;
}

export function FieldErrors({ errors }: { errors?: string[] }) {
  if (!errors || errors.length === 0) return null;
  return (
    <div className="flex flex-col gap-0.5">
      {errors.map((error) => (
        <p key={error} className="text-xs text-destructive">
          {error}
        </p>
      ))}
    </div>
  );
}

export function CharacterCounter({ length, max }: { length: number; max: number }) {
  const over = length > max;
  return (
    <span
      className={cn(
        "shrink-0 text-[11px] tabular-nums",
        over ? "text-destructive" : length >= max * 0.9 ? "text-amber-600" : "text-muted-foreground",
      )}
      title={over ? "Longer than the recommended limit. It still saves, but may not fit the layout." : undefined}
    >
      {length}/{max}
    </span>
  );
}

export function FieldShell({
  label,
  hint,
  htmlFor,
  errors,
  counter,
  aside,
  inline = false,
  children,
}: FieldShellProps) {
  if (inline) {
    return (
      <div className="flex flex-col gap-1">
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <label htmlFor={htmlFor} className="block text-[13px] text-foreground">
              {label}
            </label>
            {hint ? (
              <p className="mt-0.5 line-clamp-2 text-xs text-muted-foreground" title={hint}>
                {hint}
              </p>
            ) : null}
          </div>
          {children}
        </div>
        <FieldErrors errors={errors} />
      </div>
    );
  }
  return (
    <div className="flex min-w-0 flex-col gap-1.5">
      <div className="flex items-end justify-between gap-2">
        <div className="min-w-0">
          <label htmlFor={htmlFor} className="block text-xs text-muted-foreground">
            {label}
          </label>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {aside}
          {counter ? <CharacterCounter length={counter.length} max={counter.max} /> : null}
        </div>
      </div>
      {children}
      {hint ? (
        <p className="line-clamp-2 text-[11px] leading-snug text-muted-foreground/80" title={hint}>
          {hint}
        </p>
      ) : null}
      <FieldErrors errors={errors} />
    </div>
  );
}
